// /admin/email API surface (M3, motiflink §4.5).

import type { email as emailSchemas } from '@fajrarabi/zod-schemas';
import type { z } from 'zod';

import { api } from './api';

export interface EmailTemplateSummary {
  id: string;
  slug: string;
  languageCode: string;
  name: string;
  subject: string;
  isActive: boolean;
  updatedAt: string;
}

export interface EmailTemplate extends EmailTemplateSummary {
  htmlBody: string;
  createdAt: string;
}

export interface EmailLogRow {
  id: string;
  messageId: string;
  templateSlug: string;
  recipientEmail: string;
  status: 'sent' | 'failed' | 'suppressed';
  errorMessage: string | null;
  metadata: Record<string, unknown>;
  sentAt: string | null;
  createdAt: string;
}

export interface SuppressedRow {
  id: string;
  email: string;
  reason: 'bounced' | 'unsubscribed' | 'complaint' | 'manual';
  createdAt: string;
}

export interface EmailSettingsRow {
  id: string;
  companyName: string;
  logoUrl: string | null;
  tagline: string | null;
  websiteUrl: string | null;
  contactEmail: string | null;
  footerText: string | null;
  updatedAt: string;
}

export interface TestSendResult {
  ok: boolean;
  transport: string;
  durationMs: number;
  error: string | null;
  provider: unknown;
}

export type UpdateEmailTemplateInput = z.input<typeof emailSchemas.updateEmailTemplateInput>;
export type UpdateEmailSettingsInput = z.input<typeof emailSchemas.updateEmailSettingsInput>;

type Envelope<T> = { data: T };

interface Page<T> {
  rows: T[];
  total: number;
  limit: number;
  offset: number;
}

function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    // Query params are always scalars — an object here would serialise to
    // "[object Object]" and silently produce a wrong query.
    if (v !== undefined && v !== null && v !== '') {
      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      search.set(k, String(v));
    }
  }
  return search.size > 0 ? `?${search.toString()}` : '';
}

// ─── templates ──────────────────────────────────────────────────────────────

export async function listEmailTemplates(
  lang?: string,
  signal?: AbortSignal,
): Promise<EmailTemplateSummary[]> {
  const { data } = await api<Envelope<EmailTemplateSummary[]>>(
    `/admin/email/templates${qs({ lang })}`,
    { signal },
  );
  return data;
}

export async function getEmailTemplate(
  slug: string,
  lang: string,
  signal?: AbortSignal,
): Promise<EmailTemplate> {
  const { data } = await api<Envelope<EmailTemplate>>(
    `/admin/email/templates/${encodeURIComponent(slug)}${qs({ lang })}`,
    { signal },
  );
  return data;
}

export async function updateEmailTemplate(
  slug: string,
  lang: string,
  input: UpdateEmailTemplateInput,
): Promise<EmailTemplate> {
  const { data } = await api<Envelope<EmailTemplate>>(
    `/admin/email/templates/${encodeURIComponent(slug)}${qs({ lang })}`,
    { method: 'PATCH', body: input },
  );
  return data;
}

/** Which {{variables}} each slug understands — served by the API so the editor
 *  hints and the seeded templates cannot drift apart. */
export async function getVariableHints(signal?: AbortSignal): Promise<Record<string, string[]>> {
  const { data } = await api<Envelope<Record<string, string[]>>>('/admin/email/variable-hints', {
    signal,
  });
  return data;
}

// ─── send log ───────────────────────────────────────────────────────────────

export async function listEmailLog(
  params: { status?: string; template?: string; email?: string; limit?: number; offset?: number },
  signal?: AbortSignal,
): Promise<Page<EmailLogRow>> {
  const { data } = await api<Envelope<Page<EmailLogRow>>>(`/admin/email/log${qs(params)}`, {
    signal,
  });
  return data;
}

// ─── suppression ────────────────────────────────────────────────────────────

export async function listSuppressed(
  params: { q?: string; limit?: number; offset?: number },
  signal?: AbortSignal,
): Promise<Page<SuppressedRow>> {
  const { data } = await api<Envelope<Page<SuppressedRow>>>(
    `/admin/email/suppressed${qs(params)}`,
    { signal },
  );
  return data;
}

export async function addSuppressed(email: string): Promise<void> {
  await api('/admin/email/suppressed', {
    method: 'POST',
    body: { email, reason: 'manual' },
  });
}

export async function removeSuppressed(email: string): Promise<void> {
  await api(`/admin/email/suppressed/${encodeURIComponent(email)}`, { method: 'DELETE' });
}

// ─── test send + brand hub ──────────────────────────────────────────────────

export async function sendTestEmail(input: {
  templateSlug: string;
  to: string;
  lang: string;
}): Promise<TestSendResult> {
  const { data } = await api<Envelope<TestSendResult>>('/admin/email/test', {
    method: 'POST',
    body: input,
  });
  return data;
}

export async function getEmailSettings(signal?: AbortSignal): Promise<EmailSettingsRow> {
  const { data } = await api<Envelope<EmailSettingsRow>>('/admin/email/settings', { signal });
  return data;
}

export async function updateEmailSettings(
  input: UpdateEmailSettingsInput,
): Promise<EmailSettingsRow> {
  const { data } = await api<Envelope<EmailSettingsRow>>('/admin/email/settings', {
    method: 'PATCH',
    body: input,
  });
  return data;
}
